import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { submitFormOnEnter } from '@databases/form/Form';
import { DatabaseFieldType, NumberFormats } from '@databases/types/database';
import { getNumberDisplayString } from '@databases/utils/database/field-types/number';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { Container } from './TextValueEdit';
import { MAX_POPOVER_EDIT_WIDTH } from './popover-edit-common';
import { EditComponentProps } from './types';

const NumberValueContainer = styled(Container)`
  * {
    text-align: right;
  }
`;

const textAreaStyling = xcss({
  boxSizing: 'border-box',
  minHeight: '100%',
  height: '100%',
});

const expandToTheLeftStyling = xcss({
  display: 'flex',
  flexDirection: 'row-reverse',
  boxSizing: 'border-box',
  position: 'absolute',
  zIndex: 'dialog', // Ensure container sits above blue dots
  top: 'space.0',
  right: 'space.0',
  left: 'space.0',
  bottom: 'space.0',
});

const NumberValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.NUMBER>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isInvalid,
  field,
  isDefaultValue,
  isFilterValueEdit,
}) => {
  const { locale } = useIntl();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const numberValueContainerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState(
    value !== '' ? getNumberDisplayString(value, field, locale) : ''
  );

  // Set initial minWidth, width, and maxWidth for TextArea
  useEffect(() => {
    setTextAreaWidth(textAreaRef, numberValueContainerRef);
  }, []);

  // Handle TextArea resize corresponding to input value
  useEffect(() => {
    if (textAreaRef.current && numberValueContainerRef.current) {
      if (
        textAreaRef.current.scrollWidth <=
          parseInt(textAreaRef.current.style.maxWidth, 10) &&
        textAreaRef.current.scrollWidth > parseInt(textAreaRef.current.style.minWidth, 10)
      ) {
        textAreaRef.current.style.width = '0px';
        textAreaRef.current.style.width = `${textAreaRef.current.scrollWidth}px`;
      }
    }
  }, [inputValue]);

  const setTextAreaWidth = (
    textAreaRef: React.RefObject<HTMLTextAreaElement>,
    boxRef: React.RefObject<HTMLDivElement>
  ): void => {
    if (textAreaRef.current && boxRef.current) {
      const maxWidth = Math.max(boxRef.current.offsetWidth, MAX_POPOVER_EDIT_WIDTH);

      const width = Math.min(
        MAX_POPOVER_EDIT_WIDTH,
        Math.max(textAreaRef.current.scrollWidth, boxRef.current.offsetWidth)
      );

      textAreaRef.current.style.minWidth = `${boxRef.current.offsetWidth}px`;
      textAreaRef.current.style.width = `${width}px`;
      textAreaRef.current.style.maxWidth = `${maxWidth}px`;
    }
  };

  function sanitizeNumberInputValue(inputValue: string): string {
    const isNegative = inputValue.startsWith('-');

    const sanitizedInput = inputValue.replace(/[^0-9.,]/g, '');

    return isNegative ? '-' + sanitizedInput : sanitizedInput;
  }

  const parseInput = useCallback(
    (localizedString: string) => {
      const formatter = new Intl.NumberFormat(locale);

      const parts = formatter.formatToParts(12345.6);
      let decimalSeparator = '.';
      let groupingSeparator = ',';
      for (const part of parts) {
        if (part.type === 'decimal') {
          decimalSeparator = part.value;
        } else if (part.type === 'group') {
          groupingSeparator = part.value;
        }
      }

      let withoutGroupingSeparators = localizedString.split(groupingSeparator).join('');

      // Take only the first occurrence of the decimal separator
      const decimalIndex = withoutGroupingSeparators.indexOf(decimalSeparator);
      if (decimalIndex !== -1) {
        withoutGroupingSeparators =
          withoutGroupingSeparators.slice(0, decimalIndex) +
          '.' +
          withoutGroupingSeparators
            .slice(decimalIndex + 1)
            .split(decimalSeparator)
            .join('');
      }

      return sanitizeNumberInputValue(withoutGroupingSeparators);
    },
    [locale]
  );

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>): void => {
      let parsedValue = parseInput(e.currentTarget.value);
      setInputValue(e.currentTarget.value);

      if (field.settings.numberFormat.format === NumberFormats.PERCENT) {
        parsedValue = (Number(parsedValue) / 100).toString();
      }

      onChange(parsedValue, {
        type: DatabaseFieldType.NUMBER,
        value: parsedValue,
      });
    },
    [parseInput, field.settings.numberFormat.format, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => submitFormOnEnter(e),
    []
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );

      // Ensure TextArea widths are updated on every click into cell
      setTextAreaWidth(textAreaRef, numberValueContainerRef);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handlePaste = useCallback(() => {
    // Using setTimeout to ensure the pasted content is available in the textarea
    // Setting to 10 milliseconds to maximize the amount of time while NOT being
    // perceivable to humans
    setTimeout(() => {
      setTextAreaWidth(textAreaRef, numberValueContainerRef);
    }, 10);
  }, []);

  const handleBlur = useCallback(() => {
    onBlur?.();
    // Prevents issues with incorrect TextArea width when clicking into cell
    if (textAreaRef.current) {
      textAreaRef.current.style.minWidth = `0px`;
      textAreaRef.current.style.width = `0px`;
      textAreaRef.current.style.maxWidth = `0px`;
    }
    setInputValue(value !== '' ? getNumberDisplayString(value, field, locale) : '');
  }, [field, locale, onBlur, value]);

  // Rendered in side panel when default value is toggled on or if setting a filter value
  if (isDefaultValue || isFilterValueEdit) {
    return (
      <NumberValueContainer>
        <TextArea
          id={'NumberTextArea'}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          isInvalid={isInvalid}
          autoFocus={autoFocus}
          onKeyDown={handleKeyDown}
          rows={1}
          // Workaround for some @atlaskit/textarea types being broken
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </NumberValueContainer>
    );
  }

  return (
    <Box xcss={expandToTheLeftStyling} ref={numberValueContainerRef}>
      <NumberValueContainer>
        <Box xcss={textAreaStyling}>
          <TextArea
            id={'NumberTextArea'}
            style={{
              border: `2px solid ${token(
                'color.background.accent.blue.subtle',
                '#579DFF'
              )}`,
              borderRadius: 'border.radius.100',
            }}
            className="right-align-textarea"
            ref={textAreaRef}
            value={inputValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            isInvalid={isInvalid}
            autoFocus={autoFocus}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={1}
            resize="none"
            wrap="off"
            // Workaround for some @atlaskit/textarea types being broken
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
            data-testid="number-value-edit"
          />
        </Box>
      </NumberValueContainer>
    </Box>
  );
};

export default NumberValuePopoverEdit;
